/**
 * EmailMessageView - render a filed `.eml` as a readable message.
 *
 * A matter email is stored as an encrypted `.eml` blob. Rather than stream the
 * raw bytes (which a browser cannot display), this asks the backend to decrypt
 * and PARSE it (`POST /matters/email-source`) and renders the result as a
 * message: a header block (From / To / Cc / Date / Subject), the body, and an
 * attachment list.
 *
 * Security: the HTML body of a stored email is untrusted. It is sanitised with
 * DOMPurify before it is inserted, stripping scripts, event handlers and
 * `javascript:` URLs - the same class of fix applied to the DOCX viewer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import apis from '../../../utils/apis';
import Skeleton from './Skeleton';

interface ParsedEmail {
  from: string;
  to: string;
  cc: string;
  date: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  attachments: { name: string; content_type: string; size: number }[];
}

interface Props {
  blobPath: string;
  subjectFallback?: string;
  onClose?: () => void;
}

const formatBytes = (n: number): string => {
  if (!n || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const EmailMessageView: React.FC<Props> = ({ blobPath, subjectFallback }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<ParsedEmail | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await apis({ method: 'POST', url: '/matters/email-source', data: { blob_path: blobPath } });
        if (cancelled) return;
        if (res?.data?.success && res.data.data) {
          setEmail(res.data.data as ParsedEmail);
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blobPath]);

  const safeHtml = useMemo(() => {
    if (!email?.body_html) return null;
    return DOMPurify.sanitize(email.body_html, { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] });
  }, [email?.body_html]);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton variant="prose" rows={2} />
        <Skeleton variant="card" rows={6} />
      </div>
    );
  }

  if (error || !email) {
    return (
      <div className="p-6 text-sm text-fg-muted">
        {t('Could not open this email.')}
      </div>
    );
  }

  const HeaderRow: React.FC<{ label: string; value: string }> = ({ label, value }) =>
    value ? (
      <div className="flex gap-2 text-xs">
        <span className="w-14 flex-shrink-0 font-medium text-fg-muted">{label}</span>
        <span className="flex-1 break-words text-fg-body">{value}</span>
      </div>
    ) : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header block */}
      <div className="border-b border-hairline px-5 py-4">
        <h2 className="mb-2 text-base font-semibold text-fg-strong break-words">
          {email.subject || subjectFallback || t('(no subject)')}
        </h2>
        <div className="space-y-1">
          <HeaderRow label={t('From')} value={email.from} />
          <HeaderRow label={t('To')} value={email.to} />
          <HeaderRow label={t('Cc')} value={email.cc} />
          <HeaderRow label={t('Date')} value={email.date} />
        </div>
        {email.attachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {email.attachments.map((a, i) => (
              <span
                key={`${a.name}-${i}`}
                className="inline-flex items-center gap-1 rounded border border-hairline bg-surface-sunken/70 px-2 py-1 text-xs text-fg-body"
                title={a.content_type}
              >
                📎 {a.name}
                {a.size ? <span className="text-fg-muted">· {formatBytes(a.size)}</span> : null}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-5 py-4">
        {safeHtml ? (
          <div
            className="email-body prose prose-sm max-w-none text-fg-body [&_img]:max-w-full [&_a]:text-primary-600"
            // Sanitised above with DOMPurify.
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-fg-body">
            {email.body_text || ''}
          </pre>
        )}
      </div>
    </div>
  );
};

export default EmailMessageView;
